import React, { useEffect, useState } from 'react';

import {
  appForProduct,
  appOpenUrl,
  bannerAction,
  bannerLabel,
  platformFromUserAgent,
  rememberKey,
  storeUrl,
} from './appBanner.js';
import { getConfig } from './runtime.js';

const OPEN_WAIT_MS = 1500;

function readFlag(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeFlag(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode can refuse storage. The bar still works for this visit.
  }
}

/**
 * Top row on a phone browser. Open launches the installed app.
 * Install goes to the store, which offers Update when an older copy
 * is already there. Desktop browsers do not show the row.
 */
export default function AppBanner() {
  const productKey = getConfig()?.APP?.productKey || 'taskydo';
  const app = appForProduct(productKey);
  const platform = typeof navigator === 'undefined'
    ? 'desktop'
    : platformFromUserAgent(navigator.userAgent);
  const [installed, setInstalled] = useState(null);
  const [hidden, setHidden] = useState(platform === 'desktop');

  useEffect(() => {
    if (platform === 'desktop') return undefined;
    const dismissed = Number(readFlag(rememberKey(productKey, 'dismissedUntil')) || 0);
    if (dismissed > Date.now()) {
      setHidden(true);
      return undefined;
    }
    const remembered = readFlag(rememberKey(productKey, 'installed'));
    if (remembered === '1') setInstalled(true);
    if (remembered === '0') setInstalled(false);

    let cancelled = false;
    const detector = navigator.getInstalledRelatedApps;
    if (typeof detector !== 'function') return undefined;
    detector.call(navigator).then((rows) => {
      if (cancelled || !Array.isArray(rows)) return;
      const found = rows.some((row) => row && row.id === app.androidId);
      if (rows.length === 0) return;
      setInstalled(found);
      writeFlag(rememberKey(productKey, 'installed'), found ? '1' : '0');
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [platform, productKey, app.androidId]);

  useEffect(() => {
    const previous = document.body.style.paddingTop;
    if (!hidden && platform !== 'desktop') {
      document.body.style.paddingTop = '64px';
    }
    return () => {
      document.body.style.paddingTop = previous;
    };
  }, [hidden, platform]);

  if (hidden || platform === 'desktop') return null;

  const action = bannerAction(installed);

  function dismiss() {
    writeFlag(rememberKey(productKey, 'dismissedUntil'), String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    setHidden(true);
  }

  function openStore() {
    window.location.assign(storeUrl(app, platform));
  }

  function openApp() {
    let left = false;
    const markLeft = () => {
      if (document.hidden) left = true;
    };
    document.addEventListener('visibilitychange', markLeft);
    window.location.href = appOpenUrl(app);
    window.setTimeout(() => {
      document.removeEventListener('visibilitychange', markLeft);
      if (left) {
        writeFlag(rememberKey(productKey, 'installed'), '1');
        setInstalled(true);
        return;
      }
      writeFlag(rememberKey(productKey, 'installed'), '0');
      setInstalled(false);
      openStore();
    }, OPEN_WAIT_MS);
  }

  return (
    <div
      role="region"
      aria-label={`${app.name} app`}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 64,
        padding: '8px 12px',
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        boxSizing: 'border-box',
        fontFamily: 'Inter, "Segoe UI", sans-serif',
      }}
    >
      <img
        src={app.icon}
        alt=""
        width={40}
        height={40}
        style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', background: app.color }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#1b2437', fontSize: 15 }}>{app.name}</div>
        <div style={{ color: '#5b6175', fontSize: 12 }}>
          {action === 'open' ? 'Open in the app' : 'Install or update the app'}
        </div>
      </div>
      <button
        type="button"
        onClick={action === 'install' ? openStore : openApp}
        style={{
          background: app.color,
          color: '#fff',
          border: 0,
          borderRadius: 999,
          minHeight: 36,
          padding: '0 14px',
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        {bannerLabel(action)}
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        style={{
          border: 0,
          background: 'transparent',
          color: '#5b6175',
          fontSize: 20,
          lineHeight: 1,
          padding: 4,
        }}
      >
        ×
      </button>
    </div>
  );
}
